// Capa HTTP y lógica pura de la pantalla "Generación del informe" (fase 1:
// análisis del expediente, dudas y hoja de hechos). El estado vive en TanStack
// Query (`hooks/useGeneracion.ts`); acá van los tipos, los fetch y las reglas
// que se pueden probar sin montar la pantalla.

import { apiFetch } from "./api";

/* ---------------------------------------------------------------------------
   Contrato del API (4a). Los nombres son los del servidor.
--------------------------------------------------------------------------- */

export type EstadoDeLaCorrida = "en_curso" | "ok" | "error";

export interface CorridaDelAnalisis {
  corrida_id: number;
  estado: EstadoDeLaCorrida;
  paso: { nombre: string; numero: number; de: number } | null;
  porcentaje: number;
  contadores: {
    documentos: number;
    hechos: number;
    dudas: number;
    descartados: number;
    llamadas_al_modelo: number;
  };
  pasos: {
    nombre: string;
    estado: string;
    iniciado_en: string | null;
    terminado_en: string | null;
  }[];
  lectura: {
    considerados: { nombre: string; tipo: string; recortado: boolean }[];
    pendientes: number;
    sin_usar: { nombre: string; tipo: string }[];
    fuera_del_contexto: { nombre: string; tipo: string }[];
  } | null;
  iniciada_en: string | null;
  terminada_en: string | null;
  error: { codigo: string; mensaje: string } | null;
}

export type TipoDeDuda = "contradiccion" | "faltante" | "implausible" | "descuadre" | "criterio";
export type GravedadDeDuda = "bloquea" | "advierte";
export type EstadoDeDuda = "abierta" | "respondida" | "ya_no_aparece";

export interface EvidenciaDeDuda {
  archivo_id: number | null;
  documento: string;
  ubicacion: string | null;
  fragmento: string | null;
}

export interface OpcionDeDuda {
  indice: number;
  etiqueta: string;
  clave: string | null;
  sujeto: string | null;
  valor_formateado: string | null;
  fija_un_hecho: boolean;
  /** El valor que esta opción dice que DEBE tener un dato de una pestaña: no lo escribe. */
  corrige_a?: string | null;
  corrige_campo?: string | null;
}

/** Dónde se corrige un dato derivado: una pestaña (con su fila, si se sabe) o la valorización. */
export interface DestinoDeLaCorreccion {
  tipo: "pestana" | "valorizacion";
  pestana: string | null;
  fila: { tipo: string; id: number } | null;
}

/**
 * Una respuesta que dijo cuál debe ser un dato derivado de una pestaña. `pendiente` mientras la
 * hoja no muestre el valor esperado; `cumplida` cuando ya lo muestra; `cerrada_manual` cuando
 * una persona dejó constancia de que lo corrigió de otra forma.
 */
export interface CorreccionDeDuda {
  estado: "pendiente" | "cumplida" | "cerrada_manual";
  clave: string;
  sujeto: string;
  campo: string;
  valor_esperado: string | null;
  valor_actual: string | null;
  destino: DestinoDeLaCorreccion;
  cerrada: { motivo: string | null; en: string | null; por: { id: number; nombre: string } | null } | null;
}

/** Un dato extraíble que la duda afecta: la respuesta puede traer su valor. */
export interface HechoAEscribir {
  cita: string;
  clave: string;
  campo: string;
  tipo: string;
  valor_actual: string | null;
  /** Lo que escribió la respuesta ya dada (para precargar al cambiarla). */
  valor_escrito: string | null;
}

export interface RespuestaDeDuda {
  tipo: "opcion" | "propia" | "no_aplica";
  opcion: number | null;
  texto: string | null;
  respondida_por: { id: number; nombre: string } | null;
  respondida_en: string | null;
}

export interface Duda {
  id: number;
  tipo: TipoDeDuda;
  gravedad: GravedadDeDuda;
  corto: string;
  pregunta: string;
  hechos_afectados: string[];
  evidencia: EvidenciaDeDuda[];
  opciones: OpcionDeDuda[];
  estado: EstadoDeDuda;
  vigente: boolean;
  respuesta: RespuestaDeDuda | null;
  /** Se respondió sin fijar un dato y la duda toca hechos de la valorización. */
  requiere_revisar_valorizacion: boolean;
  /** Ausente = sin corrección (un servidor anterior no la informa). */
  correccion?: CorreccionDeDuda | null;
  hechos_a_escribir?: HechoAEscribir[];
  corrida_id: number | null;
  creada_en: string | null;
}

export interface ResumenDeDudas {
  abiertas: number;
  bloquean: number;
  respondidas: number;
  ya_no_aparecen: number;
}

export type EstadoDeHecho = "confirmado" | "propuesto" | "con_duda" | "resuelto";

export interface Hecho {
  clave: string;
  sujeto: string;
  sujeto_etiqueta: string | null;
  cita: string;
  nombre: string;
  tipo: string | null;
  origen: string | null;
  valor: string | null;
  valor_formateado: string | null;
  fuente: { tipo: string; texto: string | null; ref: string | null };
  estado: EstadoDeHecho;
  confirmado_por: number | null;
  confirmado_en: string | null;
  /** Las secciones redactadas que citan este hecho (fase 3). */
  citada_por: { item_id: number; nombre: string }[];
}

/** El valor de un dato extraíble que la respuesta trae (`cita` es la de `hechos_a_escribir`). */
export interface ValorEscritoAlResponder {
  cita: string;
  valor: string;
}

export type RespuestaAUnaDuda =
  | { tipo: "opcion"; opcion: number; hechos?: ValorEscritoAlResponder[] }
  | { tipo: "propia"; texto: string; hechos?: ValorEscritoAlResponder[] }
  | { tipo: "no_aplica" };

/** «Corrección pendiente: Caudal total debe ser 48,00 l/s (hoy 995.328,00 l/s)». */
export function textoDeLaCorreccion(c: CorreccionDeDuda): string {
  return `Corrección pendiente: ${c.campo} debe ser ${c.valor_esperado ?? "—"} (hoy ${c.valor_actual ?? "sin dato"})`;
}

/** Los cuatro nombres de pestaña que el servidor puede nombrar en un destino. */
export const ETIQUETA_DE_PESTANA_DE_CORRECCION: Record<string, string> = {
  general: "Antecedentes generales",
  descripcion: "Descripción de la propiedad",
  suelos: "Suelos",
  agua: "Aguas",
  maquinaria: "Maquinaria",
  instalaciones: "Instalaciones",
  construcciones: "Construcciones",
  plantaciones: "Plantaciones",
  cbr: "CBR",
  ofertas: "Ofertas",
};

/** Qué dice el botón que lleva a corregir: la pestaña, o la fase de valorización. */
export function etiquetaDelDestino(destino: DestinoDeLaCorreccion): string {
  if (destino.tipo === "valorizacion") return "Ir a la valorización";

  return `Ir a ${ETIQUETA_DE_PESTANA_DE_CORRECCION[destino.pestana ?? ""] ?? "la pestaña"}`;
}

/* ---------------------------------------------------------------------------
   HTTP
--------------------------------------------------------------------------- */

export const INTERVALO_DE_POLL_DEL_ANALISIS_MS = 3000;

/**
 * Cada cuánto volver a preguntar por la corrida: solo mientras corre. Con el
 * análisis terminado (o sin corrida) devuelve `false` y TanStack apaga el
 * intervalo, así que una pantalla ya resuelta no sondea para siempre.
 */
export function intervaloDelAnalisis(corrida: CorridaDelAnalisis | null | undefined): number | false {
  return corrida?.estado === "en_curso" ? INTERVALO_DE_POLL_DEL_ANALISIS_MS : false;
}

export async function fetchAnalisis(informeId: number): Promise<CorridaDelAnalisis | null> {
  const r = await apiFetch<{ data: CorridaDelAnalisis | null }>(`/api/informes/${informeId}/analisis`);
  return r.data;
}

export async function iniciarAnalisisRequest(informeId: number): Promise<CorridaDelAnalisis> {
  const r = await apiFetch<{ data: CorridaDelAnalisis }>(`/api/informes/${informeId}/analisis`, {
    method: "POST",
  });
  return r.data;
}

export async function fetchDudas(informeId: number): Promise<{ data: Duda[]; resumen: ResumenDeDudas }> {
  return apiFetch<{ data: Duda[]; resumen: ResumenDeDudas }>(`/api/informes/${informeId}/dudas`);
}

export async function responderDudaRequest(
  informeId: number,
  dudaId: number,
  respuesta: RespuestaAUnaDuda,
): Promise<Duda> {
  const r = await apiFetch<{ data: Duda }>(`/api/informes/${informeId}/dudas/${dudaId}/respuesta`, {
    method: "PUT",
    body: JSON.stringify(respuesta),
  });
  return r.data;
}

/** «Lo corregí de otra forma»: cierra la corrección pendiente con un motivo obligatorio. */
export async function cerrarCorreccionRequest(informeId: number, dudaId: number, motivo: string): Promise<Duda> {
  const r = await apiFetch<{ data: Duda }>(`/api/informes/${informeId}/dudas/${dudaId}/correccion/cierre`, {
    method: "POST",
    body: JSON.stringify({ motivo }),
  });
  return r.data;
}

/** Vuelve a dejar pendiente una corrección cerrada a mano. */
export async function reabrirCorreccionRequest(informeId: number, dudaId: number): Promise<Duda> {
  const r = await apiFetch<{ data: Duda }>(`/api/informes/${informeId}/dudas/${dudaId}/correccion/cierre`, {
    method: "DELETE",
  });
  return r.data;
}

/** Una clave que una persona puede escribir a mano (las EXTRAÍBLES del vocabulario). */
export interface ClaveEditable {
  clave: string;
  nombre: string;
  tipo: string;
  /** `prohibido`: sin sujeto · `libre`: el sujeto es obligatorio y se escribe · `cerrado`: uno de `sujetos`. */
  sujeto: "prohibido" | "libre" | "cerrado";
  sujetos: string[];
}

export interface HojaDeHechos {
  data: Hecho[];
  claves_editables: ClaveEditable[];
}

export async function fetchHoja(informeId: number): Promise<HojaDeHechos> {
  return apiFetch<HojaDeHechos>(`/api/informes/${informeId}/hechos`);
}

export interface EscrituraDeHecho {
  clave: string;
  sujeto?: string;
  valor: string;
}

/** Escribe, corrige o confirma UN hecho extraíble: queda firmado y confirmado. */
export async function escribirHechoRequest(informeId: number, hecho: EscrituraDeHecho): Promise<HojaDeHechos> {
  return apiFetch<HojaDeHechos>(`/api/informes/${informeId}/hechos`, {
    method: "PUT",
    body: JSON.stringify(hecho),
  });
}

/** Quita un hecho extraíble. No restituye nada. */
export async function quitarHechoRequest(
  informeId: number,
  hecho: { clave: string; sujeto?: string },
): Promise<HojaDeHechos> {
  return apiFetch<HojaDeHechos>(`/api/informes/${informeId}/hechos`, {
    method: "DELETE",
    body: JSON.stringify(hecho),
  });
}

/**
 * Las claves que se pueden ofrecer en «Agregar dato»: las que no tienen valor
 * todavía y las que llevan sujeto (siempre admiten una fila nueva).
 */
export function clavesParaAgregar(claves: ClaveEditable[], hechos: Hecho[]): ClaveEditable[] {
  const conValor = new Set(hechos.map((h) => h.clave));
  return claves.filter((c) => c.sujeto !== "prohibido" || !conValor.has(c.clave));
}

/** ¿El 409 de "analizar" es el de "ya hay uno corriendo"? Entonces no es un error. */
export function esAnalisisEnCurso(body: string): boolean {
  try {
    return (JSON.parse(body) as { codigo?: string }).codigo === "analisis_en_curso";
  } catch {
    return false;
  }
}

/* ---------------------------------------------------------------------------
   Etiquetas
--------------------------------------------------------------------------- */

export const ETIQUETA_TIPO_DE_DUDA: Record<TipoDeDuda, string> = {
  contradiccion: "Contradicción",
  faltante: "Faltante",
  implausible: "Implausible",
  descuadre: "Descuadre",
  criterio: "Criterio",
};

export const ETIQUETA_GRAVEDAD: Record<GravedadDeDuda, string> = {
  bloquea: "Bloquea",
  advierte: "Advierte",
};

export const ETIQUETA_ESTADO_DE_HECHO: Record<EstadoDeHecho, string> = {
  confirmado: "Confirmado",
  propuesto: "Propuesto",
  con_duda: "Con duda",
  resuelto: "Resuelto",
};

/**
 * Los colores de las etiquetas, tal como los dibuja el diseño. El tono es el
 * nombre de un `Etiqueta` (`ui/etiqueta`), nunca un color suelto: así acompaña al
 * modo oscuro. "Descuadre" usa la familia naranja de "desactualizada" porque es
 * un dato que dejó de cuadrar, no un peligro.
 */
export const TONO_DE_TIPO_DE_DUDA: Record<TipoDeDuda, "neutral" | "danger" | "warning" | "info" | "desactualizada"> = {
  contradiccion: "danger",
  descuadre: "desactualizada",
  faltante: "neutral",
  implausible: "warning",
  criterio: "info",
};

export const TONO_DE_GRAVEDAD: Record<GravedadDeDuda, "danger" | "warning"> = {
  bloquea: "danger",
  advierte: "warning",
};

export const TONO_DE_ESTADO_DE_HECHO: Record<EstadoDeHecho, "neutral" | "success" | "warning" | "info"> = {
  confirmado: "success",
  propuesto: "neutral",
  con_duda: "warning",
  resuelto: "info",
};

/* ---------------------------------------------------------------------------
   Dudas
--------------------------------------------------------------------------- */

export type FiltroDeDudas = "abiertas" | "bloquean" | "respondidas" | "todas";

export const FILTROS_DE_DUDAS: { filtro: FiltroDeDudas; etiqueta: string }[] = [
  { filtro: "abiertas", etiqueta: "Abiertas" },
  { filtro: "bloquean", etiqueta: "Bloquean" },
  { filtro: "respondidas", etiqueta: "Respondidas" },
  { filtro: "todas", etiqueta: "Todas" },
];

const estaAbierta = (d: Duda) => d.estado === "abierta";

/**
 * Una duda "respondida" es la que tiene respuesta, vigente o no: la que el
 * último análisis ya no levantó conserva la suya y sigue siendo una decisión
 * del tasador.
 */
const tieneRespuesta = (d: Duda) => d.respuesta !== null;

export function filtrarDudas(dudas: Duda[], filtro: FiltroDeDudas): Duda[] {
  switch (filtro) {
    case "abiertas":
      return dudas.filter(estaAbierta);
    case "bloquean":
      return dudas.filter((d) => estaAbierta(d) && d.gravedad === "bloquea");
    case "respondidas":
      return dudas.filter(tieneRespuesta);
    case "todas":
      return dudas;
  }
}

export function conteoDeDudas(dudas: Duda[]): Record<FiltroDeDudas, number> {
  return {
    abiertas: filtrarDudas(dudas, "abiertas").length,
    bloquean: filtrarDudas(dudas, "bloquean").length,
    respondidas: filtrarDudas(dudas, "respondidas").length,
    todas: dudas.length,
  };
}

/** Lo que dijo la persona, tal como se lee: la etiqueta de la opción, su texto o "No aplica". */
export function textoDeLaRespuesta(duda: Duda): string | null {
  const r = duda.respuesta;
  if (r === null) return null;

  switch (r.tipo) {
    case "opcion":
      return duda.opciones.find((o) => o.indice === r.opcion)?.etiqueta ?? null;
    case "propia":
      return r.texto;
    case "no_aplica":
      return "No aplica";
  }
}

/** La siguiente duda ABIERTA después de `idActual`, dando la vuelta; nunca la misma. */
export function siguienteDudaAbierta(dudas: Duda[], idActual: number): Duda | null {
  const posicion = dudas.findIndex((d) => d.id === idActual);
  const ordenadas = [...dudas.slice(posicion + 1), ...dudas.slice(0, Math.max(posicion, 0))];

  return ordenadas.find((d) => estaAbierta(d) && d.id !== idActual) ?? null;
}

/**
 * "Afecta a": las claves de citación que trae la duda, dichas con el nombre del
 * hecho. Una clave que la hoja no conoce se muestra cruda: esconderla ocultaría
 * justamente lo que no cuadra.
 */
export function etiquetasDeLosHechosAfectados(citas: string[], hechos: Hecho[]): string[] {
  return citas.map((cita) => {
    const hecho = hechos.find((h) => h.cita === cita);
    if (!hecho) return cita;

    return hecho.sujeto_etiqueta ? `${hecho.nombre} (${hecho.sujeto_etiqueta})` : hecho.nombre;
  });
}

/* ---------------------------------------------------------------------------
   Errores del análisis
--------------------------------------------------------------------------- */

export interface ErrorLegible {
  titulo: string;
  detalle: string;
  /** Reintentar tiene sentido. El rechazo de credencial no se arregla así. */
  reintentable: boolean;
}

export function errorDelAnalisis(error: { codigo: string; mensaje: string }): ErrorLegible {
  switch (error.codigo) {
    case "sin_nada_que_analizar":
      return {
        titulo: "No hay nada que analizar todavía",
        detalle:
          "El expediente no tiene documentos legibles ni notas de terreno. Sube documentos, o espera a que terminen de convertirse, y vuelve a analizar.",
        reintentable: true,
      };
    case "proveedor_rechazo":
      return {
        titulo: "El servicio de IA rechazó la credencial del servidor",
        detalle: "Avisa a soporte: volver a intentarlo no lo va a resolver.",
        reintentable: false,
      };
    case "proveedor_no_respondio":
      return {
        titulo: "El servicio de IA no respondió",
        detalle: "Vuelve a intentarlo en unos minutos.",
        reintentable: true,
      };
    case "tiempo_agotado":
      return {
        titulo: "El análisis tardó más de lo permitido",
        detalle: "Se detuvo. Vuelve a intentarlo; si se repite, avisa a soporte.",
        reintentable: true,
      };
    default:
      return { titulo: "El análisis no terminó", detalle: error.mensaje, reintentable: true };
  }
}

/* ---------------------------------------------------------------------------
   Carril de fases y banner de "desde su última sesión"
--------------------------------------------------------------------------- */

export type TonoDelCarril = "reposo" | "en_curso" | "bloquea" | "aviso" | "ok" | "error";

const plural = (n: number, singular: string, plural: string) => `${n} ${n === 1 ? singular : plural}`;

export function estadoDeLaFase1(
  corrida: CorridaDelAnalisis | null,
  dudas: Duda[],
): { tono: TonoDelCarril; texto: string } {
  if (corrida === null) return { tono: "reposo", texto: "Sin analizar" };
  if (corrida.estado === "en_curso") return { tono: "en_curso", texto: `Analizando · ${corrida.porcentaje} %` };
  if (corrida.estado === "error" && dudas.length === 0) return { tono: "error", texto: "El análisis falló" };

  const c = conteoDeDudas(dudas);

  if (c.bloquean > 0) {
    return {
      tono: "bloquea",
      texto: `${c.bloquean === 1 ? "1 duda bloquea" : `${c.bloquean} dudas bloquean`} · ${plural(c.abiertas, "abierta", "abiertas")}`,
    };
  }
  if (c.abiertas > 0) {
    return { tono: "aviso", texto: `${plural(c.abiertas, "advertencia abierta", "advertencias abiertas")}` };
  }
  if (c.respondidas === 0) return { tono: "ok", texto: "Completa · sin dudas" };

  return { tono: "ok", texto: `Completa · ${plural(c.respondidas, "duda respondida", "dudas respondidas")}` };
}

/** Lo que sigue abierto, dicho en frases cortas: primero lo que bloquea. */
export function pendientesDesdeLaUltimaSesion(dudas: Duda[]): { hay: boolean; frases: string[] } {
  const c = conteoDeDudas(dudas);
  const advertencias = c.abiertas - c.bloquean;
  const frases: string[] = [];

  if (c.bloquean > 0) frases.push(c.bloquean === 1 ? "1 duda bloquea" : `${c.bloquean} dudas bloquean`);
  if (advertencias > 0) frases.push(plural(advertencias, "advertencia abierta", "advertencias abiertas"));

  return { hay: frases.length > 0, frases };
}

/** A dónde lleva el botón del banner: la primera que bloquea o, si no hay, la primera abierta. */
export function primeraDudaPendiente(dudas: Duda[]): { filtro: FiltroDeDudas; dudaId: number } | null {
  const bloqueante = filtrarDudas(dudas, "bloquean")[0];
  if (bloqueante) return { filtro: "bloquean", dudaId: bloqueante.id };

  const abierta = filtrarDudas(dudas, "abiertas")[0];
  return abierta ? { filtro: "abiertas", dudaId: abierta.id } : null;
}

/* ---------------------------------------------------------------------------
   Última visita. Es una comodidad por navegador: la pantalla funciona igual
   sin ella (modo privado, datos bloqueados), por eso cada acceso va en try.
--------------------------------------------------------------------------- */

export const claveDeUltimaVisita = (usuarioId: number, informeId: number) =>
  `generacion:ultima-visita:${usuarioId}:${informeId}`;

export function leerUltimaVisita(usuarioId: number, informeId: number): Date | null {
  try {
    const guardada = localStorage.getItem(claveDeUltimaVisita(usuarioId, informeId));
    if (guardada === null) return null;

    const fecha = new Date(guardada);
    return Number.isNaN(fecha.getTime()) ? null : fecha;
  } catch {
    return null;
  }
}

export function guardarUltimaVisita(usuarioId: number, informeId: number, momento: Date): void {
  try {
    localStorage.setItem(claveDeUltimaVisita(usuarioId, informeId), momento.toISOString());
  } catch {
    // Sin almacenamiento no hay banner con fecha; la pantalla no depende de él.
  }
}
